/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { memo, useCallback } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { ContextMenu } from '@web-stories-wp/design-system';
import Proptypes from 'prop-types';
import styled from 'styled-components';

/**
 * Internal dependencies
 */
import { useQuickActions } from '../../app/highlights';
import DirectionAware from '../directionAware';
import Carousel from '../carousel';
import { useLayout } from '../../app';
import {
  CarouselArea,
  HeadArea,
  Layer,
  QuickActionsArea,
  Z_INDEX,
} from './layout';

import { useStory } from '../../app/story';
import { useDynamicCropRecorderState } from '../../elements/video/dynamicCropRecorder';
import useVideoTrim from '../videoTrim/useVideoTrim';

const DisabledChrome = styled.div.attrs(({ $isDisabled }) => ({
  'aria-disabled': $isDisabled ? 'true' : undefined,
  inert: $isDisabled ? '' : undefined,
}))`
  width: 100%;
  height: 100%;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'auto')};
`;

function NavLayer({ header }) {
  const { hasHorizontalOverflow } = useLayout(
    ({ state: { hasHorizontalOverflow } }) => ({ hasHorizontalOverflow })
  );
  const dynamicCropRecorderState = useDynamicCropRecorderState();
  const isDynamicCropRecorderActive = Boolean(
    dynamicCropRecorderState.elementId
  );
  const isAnimatedThumbnailTrimMode = useVideoTrim(
    ({ state: { isAnimatedThumbnail, isTrimMode } }) =>
      Boolean(isAnimatedThumbnail && isTrimMode)
  );
  const isVideoModeActive =
    isDynamicCropRecorderActive || isAnimatedThumbnailTrimMode;
  const { currentPage } = useStory(({ state: { currentPage } }) => {
    return {
      currentPage,
    };
  });

  const isReadonlyPage = currentPage && currentPage.readonly;

  const quickActions = useQuickActions();

  /**
   * Stop the event from bubbling if the user clicks in between buttons.
   *
   * This prevents the selected element in the canvas from losing focus.
   */
  const handleMenuBackgroundClick = useCallback((ev) => {
    ev.stopPropagation();
  }, []);

  // const showQuickActions =
  //   !hasHorizontalOverflow && Boolean(quickActions.length) && !isReadonlyPage;

  const showQuickActions = false;

  return (
    <Layer
      pointerEvents="none"
      zIndex={Z_INDEX.NAV}
      onMouseDown={(evt) => evt.stopPropagation()}
    >
      <HeadArea pointerEvents="initial">
        <DisabledChrome $isDisabled={isVideoModeActive}>
          {header}
        </DisabledChrome>
      </HeadArea>
      {showQuickActions && (
        <DirectionAware>
          <QuickActionsArea>
            <ContextMenu
              isAlwaysVisible
              isIconMenu
              disableControlledTabNavigation
              groupLabel={__(
                'Group of available options for selected element',
                'web-stories'
              )}
              items={quickActions}
              onMouseDown={handleMenuBackgroundClick}
            />
          </QuickActionsArea>
        </DirectionAware>
      )}
      <CarouselArea pointerEvents="initial">
        <DisabledChrome $isDisabled={isVideoModeActive}>
          <Carousel />
        </DisabledChrome>
      </CarouselArea>
    </Layer>
  );
}

NavLayer.propTypes = {
  header: Proptypes.node,
};

export default memo(NavLayer);
